import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import Navbar from '../components/Navbar';
import { useAuth } from '../context/AuthContext';
import { User, Lock, Save, Shield, Mail, CheckCircle, XCircle, Edit2, Download, Cloud, Clock } from 'lucide-react';
import { toast } from 'sonner';

const Settings = () => {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [twoFactorStatus, setTwoFactorStatus] = useState({ enabled: false, email_verified: false });
  const [resendingVerification, setResendingVerification] = useState(false);
  const [verificationRequired, setVerificationRequired] = useState(true);
  
  // Profile editing
  const [editingProfile, setEditingProfile] = useState(false);
  const [editName, setEditName] = useState('');
  
  // Email change
  const [showEmailChange, setShowEmailChange] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [emailChangePassword, setEmailChangePassword] = useState('');
  
  // Backup settings
  const [backupSettings, setBackupSettings] = useState({
    auto_backup_enabled: false,
    backup_frequency: 'monthly',
    backup_email: '',
    include_images: true,
    include_report: true,
    last_backup_date: null
  });
  const [backupLoading, setBackupLoading] = useState(false);

  useEffect(() => {
    fetch2FAStatus();
    fetchAppConfig();
    fetchBackupSettings();
    if (user) {
      setEditName(user.name || '');
    }
  }, [user]);

  const fetchAppConfig = async () => {
    try {
      const res = await api.get('/public/app-config');
      setVerificationRequired(res.data.email_verification_required);
    } catch (error) {
      setVerificationRequired(true);
    }
  };

  const fetch2FAStatus = async () => {
    try {
      const response = await api.get('/auth/2fa/status');
      setTwoFactorStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch 2FA status:', error);
    }
  };

  const fetchBackupSettings = async () => {
    try {
      const response = await api.get('/backup/settings');
      setBackupSettings({
        ...response.data,
        backup_email: response.data.backup_email || user?.email || ''
      });
    } catch (error) {
      console.error('Failed to fetch backup settings:', error);
    }
  };

  const handleSaveBackupSettings = async () => {
    setBackupLoading(true);
    try {
      await api.put('/backup/settings', backupSettings);
      toast.success('Backup settings saved');
    } catch (error) {
      toast.error('Failed to save backup settings');
    } finally {
      setBackupLoading(false);
    }
  };

  const handleDownloadBackup = async () => {
    setBackupLoading(true);
    try {
      toast.info('Generating backup...');
      const response = await api.get(`/backup/download?include_images=${backupSettings.include_images}`, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `finscan_backup_${new Date().toISOString().split('T')[0]}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Backup downloaded!');
    } catch (error) {
      toast.error('Failed to download backup');
    } finally {
      setBackupLoading(false);
    }
  };

  const handleSendBackupEmail = async () => {
    setBackupLoading(true);
    try {
      toast.info('Sending backup to email...');
      await api.post('/backup/send-email');
      toast.success('Backup sent to your email!');
      fetchBackupSettings(); // Refresh to get updated last_backup_date
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send backup email');
    } finally {
      setBackupLoading(false);
    }
  };

  const handleUpdateName = async () => {
    if (!editName.trim()) {
      toast.error('Name cannot be empty');
      return;
    }
    
    setLoading(true);
    try {
      await api.put('/auth/update-profile', { name: editName });
      toast.success('Name updated successfully');
      refreshUser();
      setEditingProfile(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handleChangeEmail = async (e) => {
    e.preventDefault();
    
    if (!newEmail.trim()) {
      toast.error('Please enter a new email');
      return;
    }
    
    setLoading(true);
    try {
      await api.post('/auth/change-email', {
        new_email: newEmail,
        password: emailChangePassword
      });
      toast.success('Email changed! Please check your new email for verification.');
      refreshUser();
      setShowEmailChange(false);
      setNewEmail('');
      setEmailChangePassword('');
      fetch2FAStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change email');
    } finally {
      setLoading(false);
    }
  };

  const handleToggle2FA = async () => {
    setLoading(true);
    try {
      if (twoFactorStatus.enabled) {
        await api.post('/auth/2fa/disable');
        toast.success('Two-factor authentication disabled');
      } else {
        await api.post('/auth/2fa/enable');
        toast.success('Two-factor authentication enabled');
      }
      fetch2FAStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update 2FA settings');
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    setResendingVerification(true);
    try {
      await api.post('/auth/resend-verification');
      toast.success('Verification email sent!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send verification email');
    } finally {
      setResendingVerification(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    
    setLoading(true);
    try {
      await api.post('/auth/change-password', {
        current_password: currentPassword,
        new_password: newPassword,
      });
      toast.success('Password changed successfully!');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#030712]">
      <Navbar />

      <div className="pt-24 pb-16 px-4 sm:px-6 lg:px-8 max-w-2xl mx-auto">
        <div className="mb-8">
          <h1 className="text-4xl font-bold font-['Outfit'] tracking-tight mb-2">Settings</h1>
          <p className="text-muted-foreground">Manage your account settings</p>
        </div>

        {/* Profile Info */}
        <div className="p-6 bg-white/5 border border-white/10 rounded-2xl mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-3">
              <User className="w-5 h-5 text-indigo-400" />
              <h2 className="text-lg font-semibold">Profile</h2>
            </div>
            <button
              onClick={() => setEditingProfile(!editingProfile)}
              className="text-indigo-400 hover:text-indigo-300 text-sm flex items-center space-x-1"
            >
              <Edit2 className="w-4 h-4" />
              <span>{editingProfile ? 'Cancel' : 'Edit'}</span>
            </button>
          </div>
          
          <div className="space-y-4">
            {/* Name */}
            <div>
              <label className="text-sm text-muted-foreground">Name</label>
              {editingProfile ? (
                <div className="flex items-center space-x-2 mt-1">
                  <input
                    type="text"
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    className="flex-1 px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                  <button
                    onClick={handleUpdateName}
                    disabled={loading}
                    className="px-3 py-2 bg-indigo-500 hover:bg-indigo-600 rounded-lg text-sm disabled:opacity-50"
                  >
                    Save
                  </button>
                </div>
              ) : (
                <p className="font-medium">{user?.name}</p>
              )}
            </div>
            
            {/* Email */}
            <div>
              <label className="text-sm text-muted-foreground">Email</label>
              <div className="flex items-center space-x-2">
                <p className="font-medium">{user?.email}</p>
                {twoFactorStatus.email_verified ? (
                  <span className="flex items-center text-green-400 text-xs">
                    <CheckCircle className="w-3 h-3 mr-1" /> Verified
                  </span>
                ) : (
                  <span className="flex items-center text-yellow-400 text-xs">
                    <XCircle className="w-3 h-3 mr-1" /> Not verified
                  </span>
                )}
              </div>
              {!showEmailChange && (
                <button
                  onClick={() => setShowEmailChange(true)}
                  className="text-indigo-400 hover:text-indigo-300 text-sm mt-2"
                >
                  Change email
                </button>
              )}
            </div>
            
            {/* Email Change Form */}
            {showEmailChange && (
              <form onSubmit={handleChangeEmail} className="mt-4 p-4 bg-white/5 border border-white/10 rounded-xl space-y-3">
                <div>
                  <label className="block text-sm text-muted-foreground mb-1">New Email</label>
                  <input
                    type="email"
                    value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)}
                    required
                    className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                <div>
                  <label className="block text-sm text-muted-foreground mb-1">Current Password</label>
                  <input
                    type="password"
                    value={emailChangePassword}
                    onChange={(e) => setEmailChangePassword(e.target.value)}
                    required
                    className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    type="submit"
                    disabled={loading}
                    className="px-4 py-2 bg-indigo-500 hover:bg-indigo-600 rounded-lg text-sm disabled:opacity-50"
                  >
                    {loading ? 'Changing...' : 'Change Email'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowEmailChange(false);
                      setNewEmail('');
                      setEmailChangePassword('');
                    }}
                    className="px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg text-sm"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            )}
            
            {/* Subscription */}
            <div>
              <label className="text-sm text-muted-foreground">Subscription</label>
              <p className="font-medium">
                <span className={`px-2 py-1 rounded text-xs ${
                  user?.subscription_status === 'active' ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'
                }`}>
                  {user?.subscription_status === 'active' ? 'Pro' : 'Free'}
                </span>
              </p>
            </div>
          </div>
        </div>

        {/* Email Verification - only show if verification is required globally */}
        {verificationRequired && !twoFactorStatus.email_verified && (
          <div className="p-6 bg-yellow-500/10 border border-yellow-500/30 rounded-2xl mb-6">
            <div className="flex items-center space-x-3 mb-4">
              <Mail className="w-5 h-5 text-yellow-400" />
              <h2 className="text-lg font-semibold text-yellow-400">Verify Your Email</h2>
            </div>
            <p className="text-sm text-muted-foreground mb-4">
              Please verify your email address to enable all features including two-factor authentication.
            </p>
            <button
              onClick={handleResendVerification}
              disabled={resendingVerification}
              className="bg-yellow-500 hover:bg-yellow-600 text-black px-4 py-2 rounded-full text-sm font-medium transition-colors disabled:opacity-50"
            >
              {resendingVerification ? 'Sending...' : 'Resend Verification Email'}
            </button>
          </div>
        )}

        {/* Two-Factor Authentication */}
        <div className="p-6 bg-white/5 border border-white/10 rounded-2xl mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-3">
              <Shield className="w-5 h-5 text-indigo-400" />
              <h2 className="text-lg font-semibold">Two-Factor Authentication</h2>
            </div>
            <span className={`px-2 py-1 rounded text-xs ${
              twoFactorStatus.enabled ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'
            }`}>
              {twoFactorStatus.enabled ? 'Enabled' : 'Disabled'}
            </span>
          </div>
          
          <p className="text-sm text-muted-foreground mb-4">
            Add an extra layer of security to your account. When enabled, you'll receive a verification code via email each time you log in.
          </p>
          
          <button
            onClick={handleToggle2FA}
            disabled={loading || !twoFactorStatus.email_verified}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors disabled:opacity-50 ${
              twoFactorStatus.enabled 
                ? 'bg-red-500/20 text-red-400 hover:bg-red-500/30' 
                : 'bg-green-500/20 text-green-400 hover:bg-green-500/30'
            }`}
          >
            {loading ? 'Updating...' : twoFactorStatus.enabled ? 'Disable 2FA' : 'Enable 2FA'}
          </button>
          
          {!twoFactorStatus.email_verified && (
            <p className="text-xs text-yellow-400 mt-2">
              Verify your email first to enable 2FA
            </p>
          )}
        </div>

        {/* Change Password */}
        <div className="p-6 bg-white/5 border border-white/10 rounded-2xl">
          <div className="flex items-center space-x-3 mb-4">
            <Lock className="w-5 h-5 text-indigo-400" />
            <h2 className="text-lg font-semibold">Change Password</h2>
          </div>
          
          <form onSubmit={handleChangePassword} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-muted-foreground mb-2">
                Current Password
              </label>
              <input
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                required
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-muted-foreground mb-2">
                New Password
              </label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                minLength={8}
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Min 8 characters, 1 uppercase, 1 lowercase, 1 digit
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-muted-foreground mb-2">
                Confirm New Password
              </label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                minLength={8}
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="bg-indigo-500 hover:bg-indigo-600 text-white px-6 py-3 rounded-full font-medium transition-colors flex items-center space-x-2 disabled:opacity-50"
            >
              <Save className="w-5 h-5" />
              <span>{loading ? 'Saving...' : 'Change Password'}</span>
            </button>
          </form>
        </div>

        {/* Backup & Export Section */}
        <div className="p-6 bg-white/5 border border-white/10 rounded-2xl">
          <div className="flex items-center space-x-3 mb-6">
            <div className="p-3 bg-green-500/20 rounded-xl">
              <Cloud className="w-6 h-6 text-green-400" />
            </div>
            <h2 className="text-lg font-semibold">Backup & Export</h2>
          </div>

          {/* Download Backup */}
          <div className="mb-6 p-4 bg-white/5 rounded-xl">
            <h3 className="font-medium mb-2">Download Backup</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Download a ZIP file containing all your receipts, income records, and a summary report.
            </p>
            <div className="flex items-center space-x-2 mb-4">
              <input
                type="checkbox"
                id="include-images"
                checked={backupSettings.include_images}
                onChange={(e) => setBackupSettings({...backupSettings, include_images: e.target.checked})}
                className="w-4 h-4 rounded"
              />
              <label htmlFor="include-images" className="text-sm">
                Include receipt images (larger file size)
              </label>
            </div>
            <button
              onClick={handleDownloadBackup}
              disabled={backupLoading}
              className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center space-x-2 disabled:opacity-50"
            >
              <Download className="w-5 h-5" />
              <span>{backupLoading ? 'Generating...' : 'Download Backup ZIP'}</span>
            </button>
          </div>

          {/* Send to Email */}
          <div className="mb-6 p-4 bg-white/5 rounded-xl">
            <h3 className="font-medium mb-2">Send Backup to Email</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Send a backup ZIP to your email address.
            </p>
            <button
              onClick={handleSendBackupEmail}
              disabled={backupLoading}
              className="bg-indigo-500 hover:bg-indigo-600 text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center space-x-2 disabled:opacity-50"
            >
              <Mail className="w-5 h-5" />
              <span>{backupLoading ? 'Sending...' : 'Send Backup to Email'}</span>
            </button>
          </div>

          {/* Auto Backup Settings */}
          <div className="p-4 bg-white/5 rounded-xl">
            <h3 className="font-medium mb-4 flex items-center">
              <Clock className="w-5 h-5 mr-2 text-yellow-400" />
              Automatic Backup
            </h3>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Enable Auto Backup</p>
                  <p className="text-sm text-muted-foreground">Receive backup emails automatically</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={backupSettings.auto_backup_enabled}
                    onChange={(e) => setBackupSettings({...backupSettings, auto_backup_enabled: e.target.checked})}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-500"></div>
                </label>
              </div>

              {backupSettings.auto_backup_enabled && (
                <>
                  <div>
                    <label className="block text-sm text-muted-foreground mb-1">Backup Frequency</label>
                    <select
                      value={backupSettings.backup_frequency}
                      onChange={(e) => setBackupSettings({...backupSettings, backup_frequency: e.target.value})}
                      className="w-full p-2 bg-white/5 border border-white/10 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    >
                      <option value="daily">Daily</option>
                      <option value="weekly">Weekly</option>
                      <option value="biweekly">Every 2 Weeks</option>
                      <option value="monthly">Monthly</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm text-muted-foreground mb-1">Backup Email</label>
                    <input
                      type="email"
                      value={backupSettings.backup_email}
                      onChange={(e) => setBackupSettings({...backupSettings, backup_email: e.target.value})}
                      placeholder="your@email.com"
                      className="w-full p-2 bg-white/5 border border-white/10 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                </>
              )}

              {backupSettings.last_backup_date && (
                <p className="text-sm text-muted-foreground">
                  Last backup: {new Date(backupSettings.last_backup_date).toLocaleDateString()}
                </p>
              )}

              <button
                onClick={handleSaveBackupSettings}
                disabled={backupLoading}
                className="bg-indigo-500 hover:bg-indigo-600 text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center space-x-2 disabled:opacity-50"
              >
                <Save className="w-5 h-5" />
                <span>{backupLoading ? 'Saving...' : 'Save Backup Settings'}</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;
